import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Search, 
  CheckCircle, 
  ArrowRight, 
  Target,
  TrendingUp,
  Shield,
  Users,
  Euro,
  FileCheck,
  Briefcase,
  Award,
  AlertCircle,
  Lightbulb,
  Building2
} from 'lucide-react';

export default function TrouverAppelsOffresPage() {
  return (
    <>
      <SEO 
        title="Trouver des Appels d'Offres | Sources et Méthodes 2026"
        description="Où trouver des appels d'offres publics ? BOAMP, TED, plateformes, méthodes pour PME. Guide complet pour identifier les marchés adaptés à votre entreprise."
        path="/trouver-appels-offres"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <Breadcrumb items={[{ label: "Trouver des appels d'offres", href: "/trouver-appels-offres" }]} />
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-6">
                <Briefcase className="w-4 h-4" />
                <span>Sources et méthodes</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Trouver des Appels d'Offres
                <br />
                <span className="text-violet-300">Publics en 2026</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                BOAMP, TED, plateformes de dématérialisation : découvrez toutes les sources pour identifier les marchés publics adaptés à votre entreprise.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                    Trouver mes premiers marchés
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Les marchés publics : une opportunité majeure pour les PME
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Chaque année, la commande publique française représente plus de <strong>200 milliards d'euros</strong> de contrats. Contrairement aux idées reçues, les PME et TPE ont toute leur place dans cet écosystème. En réalité, elles représentent déjà <strong>plus de 60% des attributaires</strong> de marchés publics en nombre de contrats.
                </p>
                <p>
                  Pourtant, de nombreux dirigeants de petites entreprises hésitent encore à se lancer. Les raisons évoquées sont souvent les mêmes : manque de temps pour surveiller les publications, complexité administrative perçue, ou sentiment de ne pas être assez "gros" pour concourir.
                </p>
                <p>
                  Ce guide vous démontre que <strong>trouver des appels d'offres adaptés à votre PME</strong> est non seulement possible, mais peut devenir un véritable levier de croissance pour votre entreprise.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Où trouver les appels d'offres */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Où trouver les appels d'offres accessibles aux PME ?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: Building2,
                  title: "BOAMP",
                  desc: "Le Bulletin Officiel des Annonces des Marchés Publics publie les marchés de l'État et des collectivités. Accès gratuit sur boamp.fr."
                },
                {
                  icon: Search,
                  title: "Plateformes de dématérialisation",
                  desc: "PLACE, AWS, Maximilien, Achat Public... Chaque collectivité utilise sa propre plateforme. Inscrivez-vous pour recevoir des alertes."
                },
                {
                  icon: Target,
                  title: "Marchés des collectivités locales",
                  desc: "Mairies, communautés de communes, conseils départementaux : pensez local ! Ces marchés sont souvent plus accessibles aux PME."
                },
                {
                  icon: TrendingUp,
                  title: "Marchés des grands comptes",
                  desc: "SNCF, EDF, hôpitaux, universités... Ces entités publient régulièrement des marchés allotis accessibles aux PME."
                },
                {
                  icon: Euro,
                  title: "Marchés sous les seuils",
                  desc: "En dessous de 40 000€ HT, les acheteurs peuvent contacter directement les entreprises. Faites-vous connaître localement !"
                },
                {
                  icon: Shield,
                  title: "Marchés réservés",
                  desc: "Certains marchés sont réservés aux PME innovantes, aux structures d'insertion ou aux entreprises du secteur adapté."
                }
              ].map((source, i) => (
                <Card key={`item-${i}`} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <source.icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{source.title}</h3>
                    <p className="text-slate-600">{source.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Avantages PME */}
        <section className="py-16 bg-violet-900 text-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Les dispositifs qui favorisent l'accès des PME aux marchés publics
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    title: "Allotissement obligatoire",
                    desc: "Depuis 2016, les acheteurs publics doivent diviser leurs marchés en lots pour permettre aux PME de candidater. Un marché de 5 millions d'euros peut ainsi être découpé en 10 lots de 500 000€."
                  },
                  {
                    title: "Avances et acomptes",
                    desc: "Les PME peuvent bénéficier d'avances allant jusqu'à 60% du montant du marché (contre 30% pour les grandes entreprises). Un vrai coup de pouce pour la trésorerie."
                  },
                  {
                    title: "Simplification avec le DUME",
                    desc: "Le Document Unique de Marché Européen permet de candidater avec un simple formulaire, sans fournir tous les justificatifs administratifs dès la candidature."
                  },
                  {
                    title: "Délais de paiement réduits",
                    desc: "Les acheteurs publics ont l'obligation de payer en 30 jours maximum. Les retards génèrent automatiquement des intérêts moratoires."
                  }
                ].map((item, i) => (
                  <div key={`item-${i}`} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-violet-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                      <p className="text-slate-300">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Conseils pour PME */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              10 conseils pour réussir vos premiers marchés publics
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                {[
                  {
                    num: "1",
                    title: "Commencez petit et local",
                    desc: "Visez d'abord les marchés de votre commune ou intercommunalité. Les montants sont plus modestes et la concurrence moins rude."
                  },
                  {
                    num: "2",
                    title: "Constituez votre dossier administratif",
                    desc: "Préparez en amont tous les documents requis : Kbis, attestations fiscales et sociales, références, certifications. Vous gagnerez un temps précieux."
                  },
                  {
                    num: "3",
                    title: "Utilisez le DUME",
                    desc: "Ce document simplifié vous évite de fournir tous les justificatifs dès la candidature. Vous ne les transmettrez qu'en cas d'attribution."
                  },
                  {
                    num: "4",
                    title: "Lisez attentivement le règlement de consultation",
                    desc: "C'est LE document clé. Il détaille les critères d'attribution, les documents à fournir et les délais. Une erreur de forme peut éliminer votre offre."
                  },
                  {
                    num: "5",
                    title: "Soignez votre mémoire technique",
                    desc: "Au-delà du prix, les acheteurs évaluent votre compréhension du besoin et votre méthodologie. Un mémoire bien structuré fait la différence."
                  },
                  {
                    num: "6",
                    title: "Groupez-vous si nécessaire",
                    desc: "Si le marché dépasse vos capacités, constituez un groupement avec d'autres entreprises complémentaires."
                  },
                  {
                    num: "7",
                    title: "Sous-traitez intelligemment",
                    desc: "Vous pouvez sous-traiter une partie du marché. Mentionnez-le clairement dans votre offre avec les coordonnées du sous-traitant."
                  },
                  {
                    num: "8",
                    title: "Automatisez votre veille",
                    desc: "Consulter manuellement les sites de publication prend un temps fou. Utilisez un outil comme DCE Express pour recevoir automatiquement les marchés pertinents."
                  },
                  {
                    num: "9",
                    title: "Demandez un débriefing en cas de rejet",
                    desc: "C'est votre droit ! L'acheteur doit vous expliquer pourquoi votre offre n'a pas été retenue. C'est précieux pour progresser."
                  },
                  {
                    num: "10",
                    title: "Persévérez",
                    desc: "Les premiers échecs sont normaux. Chaque candidature vous apprend quelque chose. Les entreprises qui persistent finissent par décrocher des marchés."
                  }
                ].map((conseil, i) => (
                  <Card key={`item-${i}`} className="border-l-4 border-l-violet-500">
                    <CardContent className="p-4">
                      <div className="flex gap-4 items-start">
                        <div className="w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                          {conseil.num}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-1">{conseil.title}</h3>
                          <p className="text-slate-600">{conseil.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Erreurs à éviter */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les erreurs courantes à éviter
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  icon: AlertCircle,
                  title: "Répondre à tout",
                  desc: "Mieux vaut 5 candidatures bien travaillées que 50 réponses bâclées. Ciblez les marchés où vous avez de réelles chances."
                },
                {
                  icon: FileCheck,
                  title: "Négliger les formalités",
                  desc: "Une signature manquante, un document périmé, une date limite dépassée... Ces erreurs sont éliminatoires. Relisez tout !"
                },
                {
                  icon: Euro,
                  title: "Casser les prix",
                  desc: "Les acheteurs se méfient des offres anormalement basses. Un prix trop bas peut conduire au rejet de votre candidature."
                },
                {
                  icon: Lightbulb,
                  title: "Copier-coller sans adapter",
                  desc: "Chaque marché est unique. Personnalisez votre mémoire technique en fonction des besoins spécifiques de l'acheteur."
                }
              ].map((erreur, i) => (
                <Card key={`item-${i}`} className="border-red-200 bg-red-50/50">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <erreur.icon className="w-6 h-6 text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{erreur.title}</h3>
                        <p className="text-slate-600">{erreur.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Success stories */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Ils ont réussi : témoignages de PME
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  quote: "Avec 5 salariés, je pensais que les marchés publics n'étaient pas pour moi. J'ai commencé par les petits lots de ma communauté de communes. Aujourd'hui, 40% de mon CA vient du public.",
                  author: "Pierre B.",
                  role: "Artisan électricien, Bretagne"
                },
                {
                  quote: "Le plus dur était de trouver les bons marchés. Depuis que j'utilise un outil de veille automatisé, je ne rate plus aucune opportunité dans mon secteur.",
                  author: "Fatima K.",
                  role: "Gérante SSII, Île-de-France"
                },
                {
                  quote: "Mon premier marché gagné représentait 15 000€. Petit, mais il m'a permis de constituer une première référence. Trois ans plus tard, je viens de décrocher un marché à 200 000€.",
                  author: "Laurent M.",
                  role: "Consultant formation, Lyon"
                }
              ].map((temoignage, i) => (
                <Card key={`item-${i}`} className="border-slate-200">
                  <CardContent className="p-6">
                    <p className="text-slate-600 italic mb-4">"{temoignage.quote}"</p>
                    <div>
                      <p className="font-semibold text-slate-900">{temoignage.author}</p>
                      <p className="text-sm text-slate-500">{temoignage.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Prêt à trouver vos premiers appels d'offres ?
            </h2>
            <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">
              Notre IA identifie les marchés adaptés à votre PME. Essai gratuit, sans engagement.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                Commencer gratuitement
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="trouver" />

        <Footer />
      </div>
    </>
  );
}
